<template>
  <div class="animated">
    <v-server-table
      :columns="table_config.columns"
      :options="table_config.options"
      :theme="theme"
      :ref="ref"
      id="customer-server-table"
      class="w-100"
    >
      <div slot="beforeTable" class="pt-3">
        <b-row>
          <b-col>
            <select-filter
              variant="info"
              icon="fa-toggle-on"
              placeholder="All Active/Inactive"
              :list="active_filters"
              filter-field="inactive"
              filter-operator="eq"
              @onSelect="setCustomFilter('customer__active', $event)"
            />
          </b-col>
          <b-col class="p-0">
            <select-filter
              variant="info"
              icon="fa-map-marker"
              placeholder="All Provinces/States"
              :list="province_filters"
              filter-field="primary_billing_location.region"
              filter-operator="eq"
              @onSelect="setCustomFilter('filter__provinces', $event)"
            />
          </b-col>
          <b-col class="pl-0">
            <select-filter
              variant="info"
              icon="fa-map-marker"
              placeholder="All Cities"
              :list="city_filters"
              filter-field="primary_billing_location.city"
              filter-operator="eq"
              :disabled="!customFilters.filter__provinces"
              @onSelect="setCustomFilter('customer__cities', $event)"
            />
          </b-col>
          <b-col>
            <select-filter
              variant="info"
              icon="fa-toggle-on"
              placeholder="Credit/No-Credit"
              :list="credit_filters"
              filter-field="credit"
              filter-operator="eq"
              @onSelect="setCustomFilter('customer__credit', $event)"
            />
          </b-col>
        </b-row>
      </div>
      <span slot="full_name" slot-scope="{ row }">
        <router-link :to="`/customer/${row.id}`">
          <b>{{ row.full_name }}</b>
        </router-link>
      </span>
      <span slot="primary_billing_location.city" slot-scope="{ row }">
        <existence-gate :entity="row" :levels="table_data.city" :field="'city'">
          <template #existing-content>
            {{ getData(row, { levels: table_data.city, field: "city" }) }}
          </template>
        </existence-gate>
      </span>
      <span slot="phone" slot-scope="{ row }">
        <existence-gate
          :entity="row"
          :levels="table_data.phone"
          :field="'phone'"
        >
          <template #existing-content>
            <a
              :href="`tel:${getData(row, {
                levels: table_data.phone,
                field: 'phone',
              })}`"
              >{{
                getData(row, { levels: table_data.phone, field: "phone" })
              }}</a
            >
          </template>
        </existence-gate>
      </span>
      <span slot="last_project" slot-scope="{ row }">
        {{ row.getLastProject().publicNumber() }}
      </span>
    </v-server-table>
  </div>
</template>

<script>
import Vue from "vue";
import CustomerCollection from "@/collections/crm/CustomerCollection";
import BaseServerTable from "@/components/shared/BaseServerTable";
import ExistenceGate from "@/components/shared/ExistenceGate";
import SelectFilter from "@/components/shared/SelectFilter";
import { ServerTable } from "vue-tables-2";
import { getCollection } from "@/shared/fetch-utils";
import { getEntityData } from "@/shared/utils";

Vue.use(ServerTable);

export default {
  name: "CustomerServerTable",
  extends: BaseServerTable,
  components: {
    ServerTable,
    SelectFilter,
    ExistenceGate,
  },
  props: {
    collection: {
      type: Object,
      default: () => new CustomerCollection(),
    },
    show_columns: {
      type: Array,
      default: () => [
        "full_name",
        "account_number",
        "primary_billing_location.city",
        "phone",
        "type",
        "last_project",
        "actions",
      ],
    },
    filterable: {
      type: Array,
      default: () => ["searchable"],
    },
    sortable: {
      type: Array,
      default: () => [
        "full_name",
        "account_number",
        "primary_billing_location.city",
      ],
    },
  },
  data() {
    return {
      customer_location_collection: null,
      table_config: this.lodash.merge(this.getDefaultTableConfig(), {
        options: {
          headings: {
            full_name: "Customer Name",
            account_number: "Customer #",
            "primary_billing_location.city": "City",
            phone: "Phone #",
            last_project: "Last Project",
          },
        },
      }),
      active_filters: [
        { value: 0, text: "Active" },
        { value: 1, text: "Inactive" },
      ],
      credit_filters: [
        { value: 1, text: "Credit" },
        { value: 0, text: "No Credit" },
      ],
      province_filters: [],
      table_data: {
        city: ["primary_billing_location"],
        phone: ["primary_customer_contact"],
      },
    };
  },
  computed: {
    city_filters () {
      let cities = [];

      this.customer_location_collection.each((location) => {
        if (
          location.region === this.customFilters.filter__provinces &&
          location.city
        ) {
          cities = [
            ...cities,
            {
              value: location.city,
              text: location.city,
            },
          ];
        }
      });

      return cities.sort((a, b) => a.value.localeCompare(b.value));
    }
  },
  methods: {
    getData({ _reference }, data) {
      return getEntityData({
        entity: _reference,
        ...data,
      });
    },
    prepareCollectionFilter(filter) {
      filter.sortAddDesc("created_at", true);

      return filter;
    },
    async init() {
      this.customer_location_collection = await getCollection({
        collection_type: "customer_location",
        sort: [
          {
            logic: "sortAddAsc",
            field: "region",
          },
          {
            logic: "sortAddAsc",
            field: "city",
          },
        ],
        limit: "all",
        message: this.$bvToast,
      });

      const regions = {};

      this.customer_location_collection.each((location) => {
        if (location.region && !regions[location.region]) {
          regions[location.region] = location.region;
          this.province_filters = [
            ...this.province_filters,
            {
              value: location.region,
              text: `${location.country} - ${location.region}`,
            },
          ];
        }
      });
    },
  },
  created() {
    this.init();
  },
};
</script>
